// Internal-linking hub/spoke data (Audit C H1 — the biggest ranking lever).
//
// Plain, SERIALIZABLE data only (no `content/dataset.ndjson` import, no hooks) so
// this module is importable by BOTH the client section (index.js) and the server
// twin (Server.jsx) with zero runtime cost. It powers three cross-link kinds:
//   (a) related products — descriptive product-name anchors + a one-line "why".
//   (b) related articles — blog <-> product cross-links with a reason-to-click blurb.
//   (c) the hardware-overview <-> /product/* buy <-> /specs 3-node cluster.
//
// COPY: every anchor, descriptor and blurb below is production copy — grounded in
// the product's own shop description and each article's excerpt in
// content/dataset.ndjson, in VRS's voice (expertise + value-for-money). No
// placeholders. Anchor destinations are verified against the `route` table.
//
// HOW THIS MAP WAS DERIVED (deterministic, from content/dataset.ndjson):
//   • Cluster nodes: the `route` table joins each product line's URLs. The
//     hardware overview, the /product/* buy page and the /hardware/*/specs page
//     share a product line; the specs + checkout sections carry a stable
//     `itemType`, and the overview page is joined by its hero title. Those give
//     `overview` / `buy` / `specs` + the `itemTypes` / `heroTitles` identity keys.
//     `specs` is null for a line with no /specs route (the GT-2 cockpit) so the
//     resolver never emits a dead anchor.
//   • relatedProducts: from `shopItem.category` (same-category items) and the
//     bundles that contain the line — e.g. the pedal tuning springs + dampers for
//     the pedals line, the other Steering Wheels for a wheel, the other Wheel
//     Bases + bundles for a base, the GT-2 cockpit accessories for the cockpit.
//     Anchor text = the product's public shop name; `blurb` = a true, specific
//     reason-to-click drawn from that product's own description.
//   • relatedArticles (blog<->product): matched by product keyword in the blog
//     slug/title — "pedals" -> pro-pedals; "upgradable wheel base"/"connectivity"/
//     "qr connect" -> uDFP20. A post with NO confident product match is OMITTED
//     (never mislinked); general "get faster"/iRacing coaching posts are left out
//     of the product families on purpose.
//
// All hrefs are BARE (no region prefix); the resolver region-scopes commerce
// paths via utils/regions `normalizeInternalHref` at render time.

// Article slug -> { title, blurb } for the "Related articles" block. Titles are
// verbatim from the blogPost docs; blurbs are one-line hooks grounded in each
// post's own excerpt (blog route slug === URL).
export const ARTICLES = {
  'best-sim-racing-pedals': {
    title: 'Best Sim Racing Pedals 2026: Complete Buyer’s Guide',
    blurb:
      'Every budget compared — from entry-level load cells to the pro-grade pedals esports champions actually race.',
  },
  'in-depth-look-at-the-vrs-directforce-pro-pedals': {
    title: 'An In-depth look at the VRS DirectForce Pro Pedals',
    blurb:
      'A deep dive into the throttle, brake and clutch: response curves, adjustment ranges and why they feel the way they do.',
  },
  'Why-choose-the-VRS-upgradable-wheel-base': {
    title: 'Why choose the VRS upgradable wheel base?',
    blurb:
      'Start affordable at 6Nm and unlock up to 20Nm of direct drive torque when you’re ready — no new base required.',
  },
  'the-next-generation-of-vrs-connectivity': {
    title: 'The Next Generation of VRS Connectivity',
    blurb:
      'How QR Connect brings slip-ring quick-release connectivity to the DFP20 and DFP15 — with the full compatibility guide.',
  },
  'vrs-transitioning-to-the-new-qr-connect-system': {
    title: 'VRS Transitioning to the New QR Connect System',
    blurb:
      'Meet QR Connect — the redesigned quick release that fixes NRG-style misalignment for a rock-solid wheel connection.',
  },
}

// Legacy alias: title-only lookup (kept so any older import keeps resolving).
export const ARTICLE_TITLES = Object.fromEntries(
  Object.entries(ARTICLES).map(([slug, meta]) => [slug, meta.title]),
)

// One entry per product line. `key` is internal only.
// `productsHeading` / `articlesHeading` are the context-aware block headings.
export const FAMILIES = [
  {
    key: 'pro-pedals',
    name: 'Pro Pedals',
    overview: '/hardware/pro-pedals',
    buy: '/product/pro-pedals',
    specs: '/hardware/pro-pedals/specs',
    itemTypes: ['PEDALS_THROTTLE_AND_BRAKE'],
    heroTitles: ['directforce pro sim racing pedals', 'pro pedals'],
    productsHeading: 'Tune your Pro Pedals',
    articlesHeading: 'Sim racing pedals, explained',
    articleSlugs: [
      'best-sim-racing-pedals',
      'in-depth-look-at-the-vrs-directforce-pro-pedals',
    ],
    relatedProducts: [
      {
        name: 'Throttle Spring',
        href: '/product/throttle-spring',
        blurb: 'Swap in a firmer or softer throttle spring to fine-tune resistance and travel to your driving style.',
      },
      {
        name: 'Brake Springs',
        href: '/product/brake-spring',
        blurb: 'A red and a blue spring — combine them to dial in exactly the braking stiffness and feel you want.',
      },
      {
        name: 'Clutch Spring',
        href: '/product/clutch-spring',
        blurb: 'A replacement clutch spring to tune the resistance and feel of the clutch pedal.',
      },
      {
        name: 'Throttle Damper',
        href: '/product/throttle-damper',
        blurb: 'Hydraulic damping that smooths out throttle travel for more precise, controlled inputs.',
      },
      {
        name: 'Brake or Clutch Damper',
        href: '/product/brake-clutch-damper',
        blurb: 'A hydraulic damper kit that smooths brake or clutch movement for finer control under load.',
      },
    ],
  },
  {
    key: 'lite-formula-wheel',
    name: 'Lite Formula Wheel',
    overview: '/hardware/lite-formula-wheel',
    buy: '/product/lite-formula-wheel',
    specs: '/hardware/lite-formula-wheel/specs',
    itemTypes: ['LITE_FORMULA_STEERING_WHEEL'],
    heroTitles: ['lite formula wheel'],
    productsHeading: 'Explore VRS steering wheels',
    articleSlugs: [],
    relatedProducts: [
      {
        name: 'R295 GT & Formula Steering Wheel',
        href: '/product/r295-steering-wheel',
        blurb: 'The flagship VRS wheel: short-travel RGB buttons, precision thumb encoders and SimHub-configurable rev LEDs for GT and Formula.',
      },
      {
        name: 'R295 G2 GT & Formula Steering Wheel',
        href: '/product/r295-g2-steering-wheel',
        blurb: 'The proven R295 body with a magnetic quick-release connector — snap it on and off in an instant, no wired port to wear out.',
      },
      {
        name: 'VRS x ASCHER Ultimate Wheel',
        href: '/product/vrs-ascher-ultimate-wheel',
        blurb: 'A co-branded Ascher Racing x VRS wheel: race-grade controls and premium build, plug-and-play over USB.',
      },
    ],
  },
  {
    key: 'r295-steering-wheel',
    name: 'R295 GT & Formula Steering Wheel',
    overview: '/hardware/r295-steering-wheel',
    buy: '/product/r295-steering-wheel',
    specs: '/hardware/r295-steering-wheel/specs',
    itemTypes: ['R295_STEERING_WHEEL'],
    heroTitles: ['r295 gt & formula steering wheel', 'r295 steering wheel'],
    productsHeading: 'Explore VRS steering wheels',
    articleSlugs: [],
    relatedProducts: [
      {
        name: 'R295 G2 GT & Formula Steering Wheel',
        href: '/product/r295-g2-steering-wheel',
        blurb: 'The R295 you know, upgraded with a magnetic quick release for instant wheel swaps and no port to wear out.',
      },
      {
        name: 'Lite Formula Wheel',
        href: '/product/lite-formula-wheel',
        blurb: 'A rigid, lightweight 287mm formula wheel with replaceable silicone grips and hall-sensor paddles — at an incredible price.',
      },
      {
        name: 'VRS x ASCHER Ultimate Wheel',
        href: '/product/vrs-ascher-ultimate-wheel',
        blurb: 'The co-branded Ascher Racing x VRS wheel: premium, plug-and-play USB with race-grade controls.',
      },
    ],
  },
  {
    key: 'r295-g2-steering-wheel',
    name: 'R295 G2 GT & Formula Steering Wheel',
    overview: '/hardware/r295-g2-steering-wheel',
    buy: '/product/r295-g2-steering-wheel',
    specs: '/hardware/r295-g2-steering-wheel/specs',
    itemTypes: ['R295_STEERING_WHEEL_G2'],
    heroTitles: ['r295 g2 gt & formula steering wheel', 'r295 g2 steering wheel', 'r295 g2'],
    productsHeading: 'Explore VRS steering wheels',
    articleSlugs: [],
    relatedProducts: [
      {
        name: 'R295 GT & Formula Steering Wheel',
        href: '/product/r295-steering-wheel',
        blurb: 'The original R295: short-travel RGB buttons, precision thumb encoders and SimHub rev LEDs on a fixed hub.',
      },
      {
        name: 'Lite Formula Wheel',
        href: '/product/lite-formula-wheel',
        blurb: 'A rigid, lightweight 287mm formula wheel with replaceable silicone grips and hall-sensor paddles — at an incredible price.',
      },
      {
        name: 'VRS x ASCHER Ultimate Wheel',
        href: '/product/vrs-ascher-ultimate-wheel',
        blurb: 'The co-branded Ascher Racing x VRS wheel: premium, plug-and-play USB with race-grade controls.',
      },
    ],
  },
  {
    key: 'dfp15-wheel-base',
    name: 'DFP15 Wheel Base',
    overview: '/hardware/dfp15-wheel-base',
    buy: '/product/dfp15-wheel-base',
    specs: '/hardware/dfp15-wheel-base/specs',
    itemTypes: ['DFP15_WHEEL_BASE'],
    heroTitles: ['dfp15 direct drive wheel base', 'dfp15 wheel base'],
    productsHeading: 'Compare wheel bases & bundles',
    articleSlugs: [],
    relatedProducts: [
      {
        name: 'DFP20 Wheel Base 20Nm',
        href: '/product/pro-wheel-base-20nm',
        blurb: 'Step up to the full 20Nm Small MiGE motor for the strongest, most immediate force feedback in the VRS range.',
      },
      {
        name: 'Upgradable Torque uDFP20 Wheel Base',
        href: '/product/pro-wheel-base-upgradable-torque',
        blurb: 'Start at 6Nm and upgrade torque up to 20Nm over time — the future-proof base that grows with you.',
      },
      {
        name: 'R295 + DFP15 Bundle',
        href: '/product/dfp15-r295-bundle',
        blurb: 'Save when you pair the DFP15 with the R295 wheel — a complete, ready-to-race direct drive package.',
      },
    ],
  },
  {
    key: 'dfp20-wheel-base',
    name: 'DFP20 Wheel Base',
    overview: '/hardware/dfp20-wheel-base',
    buy: '/product/pro-wheel-base-20nm',
    specs: '/hardware/dfp20-wheel-base/specs',
    itemTypes: ['WHEEL_BASE_UPGRADED_TORQUE_20'],
    heroTitles: ['dfp20 wheel base'],
    productsHeading: 'Compare wheel bases & bundles',
    articleSlugs: [],
    relatedProducts: [
      {
        name: 'DFP15 Wheel Base',
        href: '/product/dfp15-wheel-base',
        blurb: 'The compact, wireless 15Nm base with the same proven durability — ideal if you don’t need the full 20Nm.',
      },
      {
        name: 'Upgradable Torque uDFP20 Wheel Base',
        href: '/product/pro-wheel-base-upgradable-torque',
        blurb: 'Start lower and upgrade torque up to 20Nm when you’re ready — the future-proof route to full-power feedback.',
      },
      {
        name: 'R295 + DFP20 Bundle',
        href: '/product/r295-dfp20-bundle',
        blurb: 'Pair the 20Nm base with the R295 wheel and save on a complete, top-of-the-range direct drive package.',
      },
    ],
  },
  {
    key: 'udfp20-wheel-base',
    name: 'Upgradable Torque uDFP20 Wheel Base',
    overview: '/hardware/udfp20-wheel-base',
    buy: '/product/pro-wheel-base-upgradable-torque',
    specs: '/hardware/udfp20-wheel-base/specs',
    itemTypes: [
      'WHEEL_BASE_UPGRADABLE_TORQUE_6',
      'WHEEL_BASE_UPGRADABLE_TORQUE_9',
      'WHEEL_BASE_UPGRADABLE_TORQUE_12',
      'WHEEL_BASE_UPGRADABLE_TORQUE_15',
    ],
    heroTitles: ['udfp20 wheel base', 'upgradable torque udfp20 wheel base'],
    productsHeading: 'Compare wheel bases & bundles',
    articlesHeading: 'Get to know your wheel base',
    articleSlugs: [
      'Why-choose-the-VRS-upgradable-wheel-base',
      'the-next-generation-of-vrs-connectivity',
      'vrs-transitioning-to-the-new-qr-connect-system',
    ],
    relatedProducts: [
      {
        name: 'uDFP20 Torque Upgrade',
        href: '/product/udfp20-upgrades',
        blurb: 'Unlock more torque on your uDFP20 — upgrade steps take you all the way up to a full 20Nm.',
      },
      {
        name: 'DFP15 Wheel Base',
        href: '/product/dfp15-wheel-base',
        blurb: 'The compact, wireless 15Nm base with the same proven durability, at a lower entry price.',
      },
      {
        name: 'DFP20 Wheel Base 20Nm',
        href: '/product/pro-wheel-base-20nm',
        blurb: 'The fixed 20Nm base for maximum, immediate force feedback straight out of the box.',
      },
    ],
  },
  {
    key: 'gt2-cockpit',
    name: 'DirectForce GT-2 Cockpit',
    overview: '/hardware/gt2-cockpit',
    buy: '/product/gt2-cockpit',
    specs: null, // No /hardware/gt2-cockpit/specs route exists — never emit a dead anchor.
    itemTypes: [],
    heroTitles: ['directforce gt-2 cockpit', 'gt-2 cockpit', 'gt2 cockpit'],
    productsHeading: 'Kit out your GT-2 cockpit',
    articleSlugs: [],
    relatedProducts: [
      {
        name: 'VRS DirectForce GT-2 Seat',
        href: '/product/gt2-seat',
        blurb: 'The matching race seat for the GT-2, with Rally, GT and Formula positioning — EU-built for long stints.',
      },
      {
        name: 'Triple Monitor Mount (GT2-TX32)',
        href: '/product/gt2-triple-monitor-stand-tx32',
        blurb: 'A bolt-on triple mount for displays up to 32″ each, fully adjustable in height, depth and tilt.',
      },
      {
        name: 'Single Monitor Mount (GT2-SX57)',
        href: '/product/gt2-single-monitor-mount-sx57',
        blurb: 'A bolt-on mount for a single display up to 57″ ultrawide, adjustable in every direction.',
      },
      {
        name: 'Shifter / Handbrake Mount',
        href: '/product/gt2-shifter-handbrake-mount',
        blurb: 'A universal bolt-on point for a shifter, handbrake or flight controls, straight onto the GT-2 frame.',
      },
    ],
  },
]

// Build fast lookup indexes once at module load.
const bySlug = new Map()
const byItemType = new Map()
const byHeroTitle = new Map()
const byArticle = new Map()

const normTitle = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim()

for (const fam of FAMILIES) {
  for (const s of [fam.overview, fam.buy, fam.specs]) if (s) bySlug.set(stripLeadingSlash(s), fam)
  for (const it of fam.itemTypes || []) byItemType.set(it, fam)
  for (const t of fam.heroTitles || []) byHeroTitle.set(normTitle(t), fam)
  for (const a of fam.articleSlugs || []) byArticle.set(a, fam)
}

function stripLeadingSlash(s) {
  return String(s || '').replace(/^\/+|\/+$/g, '')
}

export function familyBySlug(slug) {
  if (!slug) return null
  return bySlug.get(stripLeadingSlash(slug)) || null
}

export function familyByItemType(itemType) {
  if (!itemType) return null
  return byItemType.get(itemType) || null
}

export function familyByHeroTitle(title) {
  const key = normTitle(title)
  if (!key) return null
  return byHeroTitle.get(key) || null
}

export function familyByArticleSlug(slug) {
  if (!slug) return null
  return byArticle.get(stripLeadingSlash(slug)) || null
}

// Which of a family's three cluster URLs is `slug` (bare)? null if none.
export function nodeRoleForSlug(fam, slug) {
  if (!fam || !slug) return null
  const bare = stripLeadingSlash(slug)
  if (stripLeadingSlash(fam.overview) === bare) return 'overview'
  if (stripLeadingSlash(fam.buy) === bare) return 'buy'
  if (fam.specs && stripLeadingSlash(fam.specs) === bare) return 'specs'
  if ((fam.articleSlugs || []).map(stripLeadingSlash).includes(bare)) return 'article'
  return null
}
